import { ascending, Map, nest } from "d3";
import * as qs from "qs";
import * as _ from "underscore";
import { Deployment } from "../domain/deployment";
import * as Dims from "../domain/dimension";
import {
  Headquarters,
  Industry,
  mkOrganization,
  Organization,
  OrganizationR,
  OrgId,
  ResponseR,
  Sector,
  Year,
} from "../domain/organization";
import { initialRegionsWithDefault, RegionR } from "../domain/region";
import { clone } from "../lib/clone";
import { memoize } from "../lib/memoize";
import { Meta } from "./AtlasH";

export interface Static {
  readonly years: Year[];
  readonly sectors: Sector[];
  readonly industries: Industry[];
  readonly regions: RegionR[];
  readonly headquarters: Headquarters[];
}

export interface Root {
  readonly static: Static;

  readonly year: Year;
  readonly regions: string[];
  readonly weights: any;

  readonly allOrganizations: Organization[];
  // ^ All organizations which are known in the atlas. It may include those which don't
  // have any data for the combination of year+regions.

  readonly organizations: Organization[];
  // ^ 'allOrganizations' with the year, region, and weights applied.

  readonly visibleOrganizations: Organization[];
  // ^ Subset of 'organizations' which have data for the selected year.

  orgById(orgId: OrgId): undefined | Organization;
  // ^ this.allOrganizations.find(…)

  derive<T>(f: (root: Root, arg: T) => Root, arg: T): Root;
}

interface RootImpl extends Root {
  _allOrganizations: typeof _allOrganizations;
  readonly orgnizationsWithResponses: Organization[];
  _organizationsWithResponses: typeof _organizationsWithResponses;
  _organizations: typeof _organizations;
  _visibleOrganizations: typeof _visibleOrganizations;
  _derive: typeof _derive;
}

const _allOrganizations = (
  orgs: OrganizationR[],
  allResponses: ResponseR[],
  headquarterIndex: any,
  industryIndex: any,
  sectorIndex: any,
): Organization[] => {
  const orgResponses = nest()
    .key(((x: ResponseR) => x.organization_id) as any)
    .map(allResponses);
  return orgs.map((org) =>
    mkOrganization(
      org,
      orgResponses[org.id] || [],
      headquarterIndex[org.headquarters_id],
      industryIndex[org.industry_id],
      sectorIndex[org.sector_id],
    ),
  );
};

const _organizationsWithResponses = (orgs: Organization[], year: Year, regions: string[]): Organization[] =>
  orgs.map((org) => clone(org, { year, regions }));

const _organizations = (orgs: Organization[], weights: any): Organization[] =>
  orgs.map((org) => clone(org, { weights }));

const _visibleOrganizations = (orgs: Organization[]): Organization[] => orgs.filter((x) => x.responses.length > 0);

const _derive = <T>(root: Root, f: (root: Root, arg: T) => Root, arg: T): Root => f(root, arg);

export const mkRoot = (
  deployment: Deployment,
  { years, organizations, regions }: Meta,
  allResponses: ResponseR[],
): Root => {
  const year = years[0];

  const headquarters = distinctKeyAndName(organizations, "headquarters_id", "headquarters_country", (d) => ({
    adjective_m_singular: d.headquarters_adjective_m_singular,
    adjective_m_plural: d.headquarters_adjective_m_plural,
    adjective_f_singular: d.headquarters_adjective_f_singular,
    adjective_f_plural: d.headquarters_adjective_f_plural,
    adjective_mixed_singular: d.headquarters_adjective_mixed_singular,
    adjective_mixed_plural: d.headquarters_adjective_mixed_plural,
    country: d.headquarters_country,
  }));
  const headquarterIndex = buildIndex(headquarters);

  const industries = distinctKeyAndName(organizations, "industry_id", "industry_name", (d) => ({
    article: d.industry_article,
    plural: d.industry_plural,
    number: d.industry_number,
    gender: d.industry_gender,
  }));
  const industryIndex = buildIndex(industries);

  const sectors = distinctKeyAndName(organizations, "sector_id", "sector_name", (d) => ({
    article: d.sector_article,
    plural: d.sector_plural,
    number: d.sector_number,
    gender: d.sector_gender,
  }));
  const sectorIndex = buildIndex(sectors);

  let initialRegion = regions[0].region_id;
  if (typeof window !== "undefined") {
    const q = qs.parse(window.location.search.replace(/^\?/, ""));
    if (typeof q.region === "string") {
      initialRegion = q.region;
    }
  }

  return {
    static: {
      years,
      sectors: _.values(sectorIndex),
      industries: _.values(industryIndex),
      regions,
      headquarters: _.values(headquarterIndex),
    },

    year,
    regions: initialRegionsWithDefault(deployment, regions, initialRegion),
    weights: Dims.weights(Dims.initial()),

    get allOrganizations() {
      return this._allOrganizations(organizations, allResponses, headquarterIndex, industryIndex, sectorIndex);
    },
    _allOrganizations: memoize(_allOrganizations),

    get orgnizationsWithResponses() {
      return this._organizationsWithResponses(this.allOrganizations, this.year, this.regions);
    },
    _organizationsWithResponses: memoize(_organizationsWithResponses),

    get organizations() {
      return this._organizations(this.orgnizationsWithResponses, this.weights);
    },
    _organizations: memoize(_organizations),

    get visibleOrganizations() {
      return this._visibleOrganizations(this.organizations);
    },
    _visibleOrganizations: memoize(_visibleOrganizations),

    orgById(orgId: OrgId) {
      return this.organizations.find((x) => x.id === orgId);
    },

    derive(f, arg) {
      return this._derive(this, f, arg);
    },
    _derive: memoize(_derive),
  } as RootImpl;
};

const distinctKeyAndName = <T>(data: T[], keyField: string, nameField: string, additionalFields: (x: T) => any) =>
  nest()
    .key((d: any) => d[keyField])
    .entries(data)
    .map((d) => ({
      ...additionalFields(d.values[0]),
      key: d.values[0][keyField],
      name: d.values[0][nameField],
    }))
    .sort((a: any, b: any) => ascending(a.name, b.name));

// Build a index (map) where keys are '.key' properties of the items
const buildIndex = <T>(data: T[]): Map<T> & { [k: string]: T } =>
  nest()
    .key((d: any) => d.key)
    .rollup((d: any) => d[0])
    .map(data) as any;
